import { ethers } from 'ethers';

// BSC RPC endpoints to try in order
const BSC_RPC_ENDPOINTS = [
  'https://bsc-dataseed1.binance.org/',
  'https://bsc-dataseed2.binance.org/',
  'https://bsc-dataseed3.binance.org/',
  'https://bsc-dataseed4.binance.org/',
  'https://bsc-dataseed1.defibit.io/',
  'https://bsc-dataseed2.defibit.io/',
  'https://bsc-dataseed3.defibit.io/',
  'https://bsc-dataseed4.defibit.io/',
  'https://bsc-dataseed1.ninicoin.io/',
  'https://bsc-dataseed2.ninicoin.io/',
  'https://rpc.ankr.com/bsc',
  'https://bsc.nodereal.io',
  'https://bsc-mainnet.nodereal.io/v1/64a9df0874fb4a93b9d0a3849de012d3'
];

// Test RPC endpoints for other chains
const TESTNET_RPC_ENDPOINTS = [
  'https://data-seed-prebsc-1-s1.binance.org:8545/',
  'https://data-seed-prebsc-2-s1.binance.org:8545/',
  'https://data-seed-prebsc-1-s2.binance.org:8545/',
  'https://data-seed-prebsc-2-s2.binance.org:8545/'
];

/**
 * Get the appropriate RPC endpoints for a chain ID
 */
const getRPCEndpoints = (chainId) => {
  switch (chainId) {
    case 56: // BSC Mainnet
      return BSC_RPC_ENDPOINTS;
    case 97: // BSC Testnet
      return TESTNET_RPC_ENDPOINTS;
    default:
      return ['http://127.0.0.1:8545/'];
  }
};

/**
 * Create a provider with fallback RPC endpoints
 */
export const createFallbackProvider = (chainId) => {
  const endpoints = getRPCEndpoints(chainId);
  
  if (endpoints.length === 0) {
    throw new Error(`No RPC endpoints available for chain ID: ${chainId}`);
  }

  // Create providers for each endpoint
  const providers = endpoints.map(url => {
    return new ethers.providers.JsonRpcProvider({
      url,
      timeout: 10000, // 10 second timeout
    });
  });

  // Return a FallbackProvider that automatically tries multiple providers
  return new ethers.providers.FallbackProvider(providers, 1); // quorum of 1
};

/**
 * Execute a contract call with automatic RPC fallback
 */
export const executeWithFallback = async (contractCall, chainId, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Create a fresh provider for each attempt
      const provider = createFallbackProvider(chainId);
      
      // If contractCall is a function, execute it with the new provider
      if (typeof contractCall === 'function') {
        return await contractCall(provider);
      }
      
      // Otherwise, assume it's a promise and return it
      return await contractCall;
      
    } catch (error) {
      lastError = error;
      console.warn(`RPC attempt ${attempt + 1} failed:`, error.message);
      
      // If this is a "missing trie node" or similar RPC error, try again
      if (
        error.message.includes('missing trie node') ||
        error.message.includes('Internal JSON-RPC error') ||
        error.message.includes('network timeout') ||
        error.message.includes('connection timeout') ||
        error.code === -32603 ||
        error.code === -32000
      ) {
        // Wait a bit before retry
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
      }
      
      // For other errors, don't retry
      break;
    }
  }
  
  throw lastError;
};

/**
 * Create a contract with fallback RPC support
 */
export const createFallbackContract = (address, abi, chainId) => {
  const provider = createFallbackProvider(chainId);
  return new ethers.Contract(address, abi, provider);
};

/**
 * Get token balance with automatic RPC fallback
 */
export const getTokenBalanceWithFallback = async (tokenAddress, userAddress, abi, chainId) => {
  return executeWithFallback(async (provider) => {
    const contract = new ethers.Contract(tokenAddress, abi, provider);
    
    // Get both balance and decimals in parallel
    const [balance, decimals] = await Promise.all([
      contract.balanceOf(userAddress),
      contract.decimals()
    ]);
    
    return {
      balance,
      decimals,
      formatted: ethers.utils.formatUnits(balance, decimals)
    };
  }, chainId);
};

/**
 * Test RPC endpoint connectivity
 */
export const testRPCEndpoint = async (url, timeout = 5000) => {
  try {
    const provider = new ethers.providers.JsonRpcProvider({
      url,
      timeout
    });
    
    // Try to get the latest block number
    const blockNumber = await provider.getBlockNumber();
    return {
      url,
      success: true,
      blockNumber,
      latency: Date.now()
    };
  } catch (error) {
    return {
      url,
      success: false,
      error: error.message
    };
  }
};

const rpcFallback = {
  createFallbackProvider,
  executeWithFallback,
  createFallbackContract,
  getTokenBalanceWithFallback,
  testRPCEndpoint,
  BSC_RPC_ENDPOINTS,
  TESTNET_RPC_ENDPOINTS
};

export default rpcFallback;
